/**
 * @typedef {Object} FullUserProfile
 * @property {string=} id
 * @property {string=} _id
 * @property {string=} name
 * @property {string=} email
 * @property {string=} role
 * @property {string=} employeeId
 * @property {string=} organization
 * @property {string=} jobTitle
 * @property {string=} joinDate
 * @property {boolean=} isActive
 * @property {string=} dateOfBirth
 * @property {string=} presentAddress
 * @property {string=} permanentAddress
 * @property {string=} city
 * @property {string=} state
 * @property {string=} pinCode
 * @property {string=} gender
 * @property {string=} maritalStatus
 * @property {string=} mobileNumber
 * @property {string=} alternateNumber
 * @property {string=} emergencyContactNumber
 * @property {string=} emergencyName
 * @property {string=} emergencyRelation
 * @property {string=} bloodGroup
 * @property {boolean=} physicalChallenged
 * @property {string=} panNumber
 * @property {string=} aadhaarNumber
 * @property {string=} passportNumber
 * @property {string=} pfDetails
 * @property {string=} medicalInsurance
 * @property {number|string=} payScale
 * @property {number|string=} ctcBasic
 * @property {number|string=} ctcHRA
 * @property {number|string=} ctcSpecialAllowance
 * @property {number|string=} ctcTravelAllowance
 * @property {number|string=} ctcITAllowance
 */

const DATE_FIELDS = [
  'joinDate',
  'onboardingApprovedAt',
  'dateOfBirth',
  'previousStartDate',
  'previousEndDate',
  'candidateDeclarationDate',
];

const PROFILE_DATE_FIELDS = ['dateOfBirth', 'previousStartDate', 'previousEndDate'];

const PROFILE_YEAR_FIELDS = [
  'schoolYearOfGraduation',
  'universityYearOfGraduation',
  'postGraduationYearOfGraduation',
  'highestEducationYearOfPassing',
];

const PROFILE_BOOLEAN_FIELDS = ['physicalChallenged', 'relievingLetterSubmitted'];

export const PROFILE_EDITABLE_FIELDS = [
  'name',
  'dateOfBirth',
  'presentAddress',
  'permanentAddress',
  'city',
  'state',
  'pinCode',
  'email',
  'mobileNumber',
  'alternateNumber',
  'gender',
  'maritalStatus',
  'emergencyContactNumber',
  'emergencyName',
  'emergencyRelation',
  'physicalChallenged',
  'bloodGroup',
  'jobTitle',
  'panNumber',
  'aadhaarNumber',
  'passportNumber',
  'pfDetails',
  'medicalInsurance',
  'schoolName',
  'schoolBoard',
  'schoolYearOfGraduation',
  'schoolPercentageOrCGPA',
  'universityName',
  'universityBoard',
  'universityYearOfGraduation',
  'universityPercentageOrCGPA',
  'postGraduationName',
  'postGraduationBoard',
  'postGraduationYearOfGraduation',
  'postGraduationPercentageOrCGPA',
  'highestEducationCategory',
  'highestEducationInstitutionName',
  'highestEducationBoard',
  'highestEducationYearOfPassing',
  'highestEducationPercentageOrCGPA',
  'reference1Name',
  'reference1Address',
  'reference1Mobile',
  'reference1Relationship',
  'reference2Name',
  'reference2Address',
  'reference2Mobile',
  'reference2Relationship',
  'totalExperience',
  'previousCompanyName',
  'previousCompanyLocation',
  'previousJobTitle',
  'previousSupervisorName',
  'previousSupervisorContact',
  'previousStartDate',
  'previousEndDate',
  'reasonForLeaving',
  'relievingLetterSubmitted',
];

const NUMBER_FIELDS = [
  'payScale',
  'ctcBasic',
  'ctcHRA',
  'ctcSpecialAllowance',
  'ctcTravelAllowance',
  'ctcITAllowance',
  'totalExperience',
  'schoolYearOfGraduation',
  'universityYearOfGraduation',
  'postGraduationYearOfGraduation',
  'highestEducationYearOfPassing',
  'schoolPercentageOrCGPA',
  'universityPercentageOrCGPA',
  'postGraduationPercentageOrCGPA',
  'highestEducationPercentageOrCGPA',
];

const cleanValue = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  return value;
};

const normalizeNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const getUserIdentifier = (userLike) => userLike?.id || userLike?._id || null;

export const normalizeUserProfile = (rawUser = {}) => {
  const normalized = { ...rawUser };

  const identifier = getUserIdentifier(rawUser);
  normalized.id = identifier;
  normalized._id = rawUser?._id || identifier;

  DATE_FIELDS.forEach((field) => {
    normalized[field] = normalizeDate(rawUser[field]);
  });

  NUMBER_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(rawUser, field)) {
      normalized[field] = normalizeNumber(rawUser[field]);
    }
  });

  Object.keys(normalized).forEach((field) => {
    if (!DATE_FIELDS.includes(field) && !NUMBER_FIELDS.includes(field)) {
      normalized[field] = cleanValue(normalized[field]);
    }
  });

  if (!normalized.presentAddress && normalized.address) {
    normalized.presentAddress = cleanValue(normalized.address);
  }

  return normalized;
};

export const normalizeUserProfiles = (users = []) =>
  Array.isArray(users) ? users.map((user) => normalizeUserProfile(user)) : [];

export const formatProfileDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

export const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'N/A';
  return `INR ${num.toLocaleString('en-IN')}`;
};

export const formatBoolean = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'N/A';
};


export const toTitleCaseWords = (value) => {
  const s = cleanValue(value);
  if (s == null) return '';

  const lower = (t) => t.toLocaleLowerCase('en-IN');
  const capWord = (word) => {
    if (!word) return '';
    const lo = lower(word);
    return lo.charAt(0).toLocaleUpperCase('en-IN') + lo.slice(1);
  };
  const capHyphenated = (token) => token.split('-').map(capWord).join('-');

  return s.split(/\s+/).map(capHyphenated).join(' ');
};

/** Like displayValue but applies title casing for human-readable labels. */
export const displayTitleCase = (value) => {
  if (value === 0) return '0';
  if (value === false) return 'No';
  if (value === true) return 'Yes';
  if (value === undefined || value === null || value === '') return 'N/A';
  const out = toTitleCaseWords(String(value));
  return out === '' ? 'N/A' : out;
};

export const displayValue = (value) => {
  if (value === 0) return '0';
  if (value === false) return 'No';
  if (value === true) return 'Yes';
  if (value === undefined || value === null || value === '') return 'N/A';
  return String(value);
};

export const createProfileFormState = (user = {}) => {
  const normalized = normalizeUserProfile(user);
  const formState = {};

  PROFILE_EDITABLE_FIELDS.forEach((field) => {
    if (PROFILE_BOOLEAN_FIELDS.includes(field)) {
      formState[field] = Boolean(normalized[field]);
      return;
    }

    if (PROFILE_DATE_FIELDS.includes(field)) {
      formState[field] = toDateInputValue(normalized[field]);
      return;
    }

    if (PROFILE_YEAR_FIELDS.includes(field)) {
      const year = normalized[field];
      formState[field] = year === null || year === undefined ? '' : String(year);
      return;
    }

    formState[field] = normalized[field] === null || normalized[field] === undefined ? '' : String(normalized[field]);
  });

  return formState;
};

export const buildProfileUpdatePayload = (formState = {}) => {
  const payload = {};

  PROFILE_EDITABLE_FIELDS.forEach((field) => {
    const rawValue = formState[field];

    if (PROFILE_BOOLEAN_FIELDS.includes(field)) {
      payload[field] = Boolean(rawValue);
      return;
    }

    if (PROFILE_DATE_FIELDS.includes(field)) {
      payload[field] = rawValue ? new Date(rawValue).toISOString() : null;
      return;
    }

    if (PROFILE_YEAR_FIELDS.includes(field)) {
      const trimmed = String(rawValue || '').trim();
      payload[field] = trimmed === '' ? null : Number(trimmed);
      return;
    }

    const trimmed = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    payload[field] = trimmed === '' || trimmed === undefined ? null : trimmed;
  });

  return payload;
};

/** Max lengths aligned with User schema (mongoose) to avoid opaque save failures */
export const PROFILE_FIELD_MAX_LENGTHS = {
  name: 100,
  presentAddress: 300,
  permanentAddress: 300,
  city: 100,
  pinCode: 10,
  mobileNumber: 15,
  alternateNumber: 15,
  state: 100,
  panNumber: 20,
  aadhaarNumber: 20,
  passportNumber: 20,
  pfDetails: 100,
  medicalInsurance: 150,
  bloodGroup: 10,
  emergencyContactNumber: 15,
  emergencyName: 150,
  emergencyRelation: 100,
  schoolName: 150,
  schoolBoard: 100,
  schoolPercentageOrCGPA: 20,
  universityName: 150,
  universityBoard: 100,
  universityPercentageOrCGPA: 20,
  postGraduationName: 150,
  postGraduationBoard: 100,
  postGraduationPercentageOrCGPA: 20,
  highestEducationCategory: 100,
  highestEducationInstitutionName: 150,
  highestEducationBoard: 100,
  highestEducationPercentageOrCGPA: 20,
  reference1Name: 100,
  reference1Address: 300,
  reference1Mobile: 15,
  reference1Relationship: 100,
  reference2Name: 100,
  reference2Address: 300,
  reference2Mobile: 15,
  reference2Relationship: 100,
  totalExperience: 100,
  previousCompanyName: 150,
  previousCompanyLocation: 150,
  previousJobTitle: 100,
  previousSupervisorName: 100,
  previousSupervisorContact: 150,
  reasonForLeaving: 300,
};

export const validateProfileFieldMaxLengths = (formState = {}) => {
  for (const field of PROFILE_EDITABLE_FIELDS) {
    const max = PROFILE_FIELD_MAX_LENGTHS[field];
    if (!max) continue;
    const raw = formState[field];
    if (raw === null || raw === undefined) continue;
    if (PROFILE_BOOLEAN_FIELDS.includes(field) || PROFILE_DATE_FIELDS.includes(field)) continue;
    if (PROFILE_YEAR_FIELDS.includes(field)) continue;
    const len = String(raw).length;
    if (len > max) {
      return `${field} is too long: maximum ${max} characters allowed (${len} entered)`;
    }
  }
  return null;
};

/**
 * True if the user entered something meaningful in this section (for per-section Save).
 * Checkboxes count only when checked (true).
 */
export const sectionHasAtLeastOneFilledField = (sectionFields = [], formState = {}) =>
  sectionFields.some((field) => {
    if (field.type === 'checkbox') return formState[field.name] === true;
    const v = formState[field.name];
    if (v === null || v === undefined) return false;
    return String(v).trim() !== '';
  });

export const validateProfileForm = (formState = {}) => {
  if (!String(formState.name || '').trim()) return 'Name is required';
  if (!String(formState.email || '').trim()) return 'Email is required';
  if (!String(formState.mobileNumber || '').trim()) return 'Mobile number is required';
  if (!String(formState.presentAddress || '').trim()) return 'Present address is required';
  if (!String(formState.permanentAddress || '').trim()) return 'Permanent address is required';
  if (!String(formState.city || '').trim()) return 'City is required';
  if (!String(formState.state || '').trim()) return 'State is required';
  if (!String(formState.pinCode || '').trim()) return 'Pin code is required';
  if (!String(formState.alternateNumber || '').trim()) return 'Alternate number is required';
  if (!String(formState.jobTitle || '').trim()) return 'Job title is required';
  if (!String(formState.panNumber || '').trim()) return 'PAN number is required';
  if (!String(formState.aadhaarNumber || '').trim()) return 'Aadhaar number is required';
  if (!String(formState.emergencyContactNumber || '').trim()) return 'Emergency contact number is required';

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(String(formState.email || '').trim())) return 'Please enter a valid email address';

  const gender = String(formState.gender || '').trim();
  if (!gender) return 'Gender is required';
  if (!['male', 'female', 'other'].includes(gender)) {
    return 'Gender must be male, female, or other';
  }

  const maritalStatus = String(formState.maritalStatus || '').trim();
  if (!maritalStatus) return 'Marital status is required';
  if (!['single', 'married'].includes(maritalStatus)) {
    return 'Marital status must be single or married';
  }

  for (const yearField of PROFILE_YEAR_FIELDS) {
    const yearValue = String(formState[yearField] || '').trim();
    if (!yearValue) continue;

    if (!/^\d{4}$/.test(yearValue)) {
      return `${yearField} must be a 4-digit year`;
    }
  }

  const maxLenError = validateProfileFieldMaxLengths(formState);
  if (maxLenError) return maxLenError;

  return null;
};
